import _extends from '@babel/runtime/helpers/extends';
import React, { useState, useCallback, useMemo, createContext, useContext } from 'react';
import PropTypes from 'prop-types';
import Icon from './Icon.js';
import { T as Tooltip } from './Tooltip-c491c7d6.js';
import { G as Group } from './Group-0add3181.js';
import { p as props } from './componentEl-fcbc301e.js';
import { s as setIcon } from './icons-3c94a1f0.js';
import { g as getZIndex, z as zIndexProps } from './zIndex-60238fbe.js';
import { a as addClassNames } from './classNames-1ed461aa.js';

function FabAdditional({
  children
}) {
  return /*#__PURE__*/React.createElement(Group, {
    direction: "column",
    align: "center",
    className: "st_btn--fab-group",
    gap: "md"
  }, children);
}

const FabContext = /*#__PURE__*/createContext();
function FabButtonItem({
  icon,
  iconSize: iconSizeProp,
  simple,
  toolTipContent,
  inverted,
  children,
  componentEl: Component,
  componentRef,
  ...otherProps
}) {
  let button;
  const componentProps = otherProps;
  if (Component === 'button' && !otherProps.type) componentProps.type = 'button';
  const {
    isHover,
    toolTipType
  } = useContext(FabContext);
  const iconSize = iconSizeProp || (simple ? 'md' : 'mlg');
  const buttonIcon = setIcon(Icon, icon, {
    size: iconSize
  });
  if (toolTipContent) {
    button = /*#__PURE__*/React.createElement(Tooltip, {
      key: `st_btn--fab-tooltip-${otherProps.key || 'default'}`,
      type: toolTipType === 'all' && isHover ? 'permanent' : 'default',
      delay: "none"
    }, /*#__PURE__*/React.createElement(Tooltip.Content, {
      position: "left"
    }, toolTipContent), /*#__PURE__*/React.createElement(Tooltip.Trigger, null, /*#__PURE__*/React.createElement(Component, _extends({
      type: "button",
      className: `st_btn--fab ${inverted ? 'st_btn--fab-inverted' : ''}`,
      ref: componentRef
    }, componentProps), buttonIcon)));
  } else {
    button = /*#__PURE__*/React.createElement(Component, _extends({
      className: `st_btn--fab ${inverted ? 'st_btn--fab-inverted' : ''}`,
      ref: componentRef
    }, componentProps), buttonIcon);
  }
  return button;
}
function FabButton({
  icon,
  iconSize: iconSizeProp,
  simple,
  toolTipContent,
  inverted,
  children,
  zIndex,
  componentEl,
  componentRef,
  toolTipType,
  bottom,
  top,
  left,
  right,
  ...otherProps
}) {
  const [isHover, setIsHover] = useState();
  const handleEnter = useCallback(() => {
    setIsHover(true);
  }, []);
  const handleLeave = useCallback(() => {
    setIsHover(false);
  }, []);
  const contextValues = useMemo(() => ({
    toolTipType,
    isHover
  }), [toolTipType, isHover]);
  const button = /*#__PURE__*/React.createElement(FabButtonItem, _extends({
    icon: icon,
    iconSize: iconSizeProp,
    simple: simple,
    toolTipContent: toolTipContent,
    inverted: inverted,
    componentEl: componentEl,
    componentRef: componentRef
  }, otherProps), children);
  if (simple) {
    return button;
  }
  let cssClasses = ['st_btn--fab-container'];
  if (zIndex) cssClasses = addClassNames(getZIndex(zIndex), cssClasses);
  if (toolTipType) cssClasses.push();
  const containerProps = {
    style: {}
  };
  if (top) containerProps.style['--st-fab-top'] = top;
  if (bottom) containerProps.style['--st-fab-bottom'] = bottom;
  if (left) containerProps.style['--st-fab-left'] = left;
  if (right) containerProps.style['--st-fab-right'] = right;
  if (toolTipType === 'all') {
    containerProps.onMouseEnter = handleEnter;
    containerProps.onMouseLeave = handleLeave;
  }
  return /*#__PURE__*/React.createElement("div", _extends({
    className: cssClasses.join(' ')
  }, containerProps), /*#__PURE__*/React.createElement(FabContext.Provider, {
    value: contextValues
  }, /*#__PURE__*/React.createElement(Group, {
    direction: "column-reverse",
    gap: "md",
    align: "center",
    className: "st_btn--fab-inner"
  }, button, children)));
}
FabButton.propTypes = {
  /**  name of icon */
  icon: PropTypes.oneOfType([PropTypes.string, PropTypes.func]).isRequired,
  /** icon size */
  iconSize: PropTypes.oneOf(['sm', 'smd', 'md', 'mlg', 'lg']),
  /** dom element or component to use for outermost wrapper */
  componentEl: props,
  /** ref for element */
  componentRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({
    current: PropTypes.any
  })]),
  /** boolen to specify if just the button is returned */
  simple: PropTypes.bool,
  /** Changes inverts the style */
  inverted: PropTypes.bool,
  /** Tooltip Content */
  toolTipContent: PropTypes.string,
  /** Behavior of tooltip */
  toolTipType: PropTypes.oneOf(['default', 'all']),
  /**
   * z-index for button container.
   * Ignored for single buttons. One of `minor`, `mid`, `mad`, `major`, `top` */
  zIndex: zIndexProps,
  /** override CSS bottom position of button */
  bottom: PropTypes.string,
  /** override CSS right position of button */
  right: PropTypes.string,
  /** override CSS top position of button */
  top: PropTypes.string,
  /** override CSS left position of button */
  left: PropTypes.string
};
FabButton.defaultProps = {
  iconSize: null,
  componentEl: 'button',
  componentRef: null,
  simple: false,
  inverted: false,
  toolTipContent: '',
  toolTipType: 'default',
  zIndex: 'major',
  bottom: 'var(--st-spacer-lg)',
  right: 'var(--st-spacer-lg)',
  top: 'auto',
  left: 'auto'
};
FabButton.FabAdditional = FabAdditional;
FabButton.FabAdditional.displayName = 'FabButton.FabAdditional';

export { FabButton as F };
